import React, { useContext, useEffect, useMemo, useState } from "react";
import { FiPlus, FiUsers, FiImage } from "react-icons/fi";
import AppShell from "../components/layout/AppShell";
import CommunityCard from "../components/common/CommunityCard";
import { AuthContext } from "../context/AuthContext";
import { CommunityContext } from "../context/CommunityContext";
import { useToast } from "../context/ToastContext";
import { Button, Input, Textarea, Modal, SearchInput, EmptyState, CardSkeleton, Tabs } from "../components/ui";
import { getCommunities, createCommunity } from "../api/communities";

export default function Communities() {
  const { isAdmin } = useContext(AuthContext);
  const { joinedCommunities, loading: joinedLoading } = useContext(CommunityContext);
  const { notify } = useToast();
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [activeTab, setActiveTab] = useState("discover");
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ name: "", description: "" });
  const [imageFile, setImageFile] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => {
    setLoading(true);
    getCommunities().then(res => setCommunities(res.data?.data || [])).catch(() => notify("Couldn't load communities.", "error")).finally(() => setLoading(false));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const joinedIds = useMemo(() => new Set(joinedCommunities.map(c => c.id)), [joinedCommunities]);

  const discover = useMemo(() => {
    const list = communities.filter(c => !joinedIds.has(c.id));
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter(c => c.name?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q));
  }, [communities, joinedIds, query]);

  const myComms = useMemo(() => {
    const list = joinedCommunities;
    if (!query.trim()) return list;
    const q = query.toLowerCase();
    return list.filter(c => c.name?.toLowerCase().includes(q));
  }, [joinedCommunities, query]);

  const handleCreate = async () => {
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name.trim()); fd.append("description", form.description.trim());
      if (imageFile) fd.append("image", imageFile);
      await createCommunity(fd);
      notify("Community created.", "success");
      setModalOpen(false); setForm({ name: "", description: "" }); setImageFile(null);
      load();
    } catch { notify("Couldn't create the community.", "error"); }
    finally { setCreating(false); }
  };

  const tabs = [
    { value: "discover", label: "Discover", count: discover.length },
    { value: "mine", label: "My communities", count: joinedCommunities.length },
  ];

  const display = activeTab === "mine" ? myComms : discover;

  return (
    <AppShell title="Communities" subtitle="Find your people and start collaborating" wide>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <SearchInput value={query} onChange={setQuery} placeholder="Search communities…" className="sm:max-w-xs" />
        {isAdmin && <Button onClick={() => setModalOpen(true)}><FiPlus className="h-4 w-4" /> Create community</Button>}
      </div>
      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} className="mb-6" />

      {loading || joinedLoading ? (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 6 }).map((_, i) => <CardSkeleton key={i} />)}</div>
      ) : display.length === 0 ? (
        <EmptyState
          icon={<FiUsers className="h-6 w-6" />}
          title={query ? "No communities match your search" : activeTab === "mine" ? "You haven't joined any communities yet" : "No communities available"}
          description={query ? "Try a different search term." : activeTab === "mine" ? "Discover communities and send a join request." : isAdmin ? "Be the first to create a community." : "Check back soon."}
          action={!query && activeTab !== "mine" && isAdmin && <Button onClick={() => setModalOpen(true)}><FiPlus className="h-4 w-4" /> Create community</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {display.map(c => <CommunityCard key={c.id} community={c} />)}
        </div>
      )}

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title="Create a community"
        footer={<><Button variant="secondary" onClick={() => setModalOpen(false)} disabled={creating}>Cancel</Button><Button onClick={handleCreate} loading={creating} disabled={!form.name.trim()}>Create community</Button></>}>
        <div className="space-y-4">
          <Input label="Community name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
          <Textarea label="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Cover image</label>
            <label className="flex h-24 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 bg-slate-50 text-slate-400 hover:border-primary-300 hover:text-primary-500">
              <FiImage className="h-5 w-5" />
              <span className="text-xs">{imageFile ? imageFile.name : "Click to upload"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files?.[0] || null)} />
            </label>
          </div>
        </div>
      </Modal>
    </AppShell>
  );
}
